import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export interface ImageData { base64: string; mediaType: string; }
export interface DocContent { text: string; images: ImageData[]; }

// ── Readers ───────────────────────────────────────────────────────────────────

function readAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = e => resolve(e.target!.result as string);
    r.onerror = reject;
    r.readAsText(file);
  });
}

function readAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = e => resolve(e.target!.result as ArrayBuffer);
    r.onerror = reject;
    r.readAsArrayBuffer(file);
  });
}

// ── PDF ───────────────────────────────────────────────────────────────────────

async function extractPdfContent(buffer: ArrayBuffer): Promise<DocContent> {
  const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(buffer) }).promise;

  // Try text extraction first
  const textPages: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items.map((item: any) => item.str ?? '').join(' ').replace(/\s+/g, ' ').trim();
    if (pageText) textPages.push(pageText);
  }
  const text = textPages.join('\n\n');
  if (text.length >= 100) return { text, images: [] };

  // Scanned/image-based PDF — render each page as a JPEG
  const images: ImageData[] = [];
  for (let i = 1; i <= Math.min(pdf.numPages, 10); i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext('2d')!, viewport, canvas }).promise;
    images.push({ base64: canvas.toDataURL('image/jpeg', 0.85).split(',')[1], mediaType: 'image/jpeg' });
  }
  return { text: '', images };
}

// ── ZIP helpers (docx / xlsx are ZIP archives) ────────────────────────────────

async function decompressDeflateRaw(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  const ds = new (window as any).DecompressionStream('deflate-raw');
  const out = await new Response((new Blob([data]).stream() as any).pipeThrough(ds)).arrayBuffer();
  return new Uint8Array(out) as Uint8Array<ArrayBuffer>;
}

async function readZipEntry(buffer: ArrayBuffer, targetName: string): Promise<Uint8Array<ArrayBuffer> | null> {
  const view = new Uint8Array(buffer);
  let i = 0;
  while (i < view.length - 30) {
    if (view[i] !== 0x50 || view[i+1] !== 0x4B || view[i+2] !== 0x03 || view[i+3] !== 0x04) { i++; continue; }
    const compression  = view[i+8]  | (view[i+9]  << 8);
    const compressedSz = view[i+18] | (view[i+19] << 8) | (view[i+20] << 16) | (view[i+21] << 24);
    const filenameLen  = view[i+26] | (view[i+27] << 8);
    const extraLen     = view[i+28] | (view[i+29] << 8);
    const dataStart    = i + 30 + filenameLen + extraLen;
    const name         = new TextDecoder().decode(view.slice(i + 30, i + 30 + filenameLen));
    if (name === targetName) {
      const slice = buffer.slice(dataStart, dataStart + compressedSz);
      if (compression === 0) return new Uint8Array(slice);
      if (compression === 8) return decompressDeflateRaw(new Uint8Array(slice) as Uint8Array<ArrayBuffer>);
      return null;
    }
    i = dataStart + (compressedSz || 1);
  }
  return null;
}

function listZipEntries(buffer: ArrayBuffer): string[] {
  const view = new Uint8Array(buffer);
  const names: string[] = [];
  let i = 0;
  while (i < view.length - 30) {
    if (view[i] !== 0x50 || view[i+1] !== 0x4B || view[i+2] !== 0x03 || view[i+3] !== 0x04) { i++; continue; }
    const compressedSz = view[i+18] | (view[i+19] << 8) | (view[i+20] << 16) | (view[i+21] << 24);
    const filenameLen  = view[i+26] | (view[i+27] << 8);
    const extraLen     = view[i+28] | (view[i+29] << 8);
    const dataStart    = i + 30 + filenameLen + extraLen;
    names.push(new TextDecoder().decode(view.slice(i + 30, i + 30 + filenameLen)));
    i = dataStart + (compressedSz || 1);
  }
  return names;
}

// ── Word (.docx) ──────────────────────────────────────────────────────────────

const IMAGE_MIME: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
  gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp',
};

async function extractDocxContent(buffer: ArrayBuffer): Promise<DocContent> {
  const xmlBytes = await readZipEntry(buffer, 'word/document.xml');
  if (!xmlBytes) throw new Error('Could not read word/document.xml — is this a valid .docx file?');

  const text = new TextDecoder().decode(xmlBytes)
    .replace(/<w:p[\s>]/g, '\n')
    .replace(/<w:t[^>]*>([\s\S]*?)<\/w:t>/g, (_, t) => t)
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
    .replace(/\n{3,}/g, '\n\n').trim();

  const images: ImageData[] = [];
  for (const name of listZipEntries(buffer).filter(n => n.startsWith('word/media/')).slice(0, 5)) {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    const mediaType = IMAGE_MIME[ext];
    if (!mediaType) continue;
    const bytes = await readZipEntry(buffer, name);
    if (!bytes) continue;
    let binary = '';
    for (const b of bytes) binary += String.fromCharCode(b);
    images.push({ base64: btoa(binary), mediaType });
  }

  return { text, images };
}

// ── Excel (.xlsx) ─────────────────────────────────────────────────────────────

async function extractXlsxContent(buffer: ArrayBuffer): Promise<DocContent> {
  const ssBytes = await readZipEntry(buffer, 'xl/sharedStrings.xml');
  const sharedStrings: string[] = [];
  if (ssBytes) {
    const ssXml = new TextDecoder().decode(ssBytes);
    for (const m of ssXml.matchAll(/<si>[\s\S]*?<\/si>/g))
      sharedStrings.push(m[0].replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim());
  }
  let text = '';
  for (let s = 1; s <= 10; s++) {
    const sheetBytes = await readZipEntry(buffer, `xl/worksheets/sheet${s}.xml`);
    if (!sheetBytes) break;
    const sheetXml = new TextDecoder().decode(sheetBytes);
    text += `\n--- Sheet ${s} ---\n`;
    for (const row of (sheetXml.match(/<row[^>]*>[\s\S]*?<\/row>/g) || [])) {
      const values = (row.match(/<c[^>]*>[\s\S]*?<\/c>/g) || []).map(cell => {
        const t = (cell.match(/\bt="([^"]+)"/) || [])[1] || '';
        const v = (cell.match(/<v>([\s\S]*?)<\/v>/) || [])[1] || '';
        return t === 's' ? (sharedStrings[parseInt(v)] || '') : v;
      }).filter(Boolean);
      if (values.length) text += values.join('\t') + '\n';
    }
  }
  return { text: text.trim(), images: [] };
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk)
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(binary);
}

async function extractImagesFromZip(
  buffer: ArrayBuffer,
  mediaPrefix: string
): Promise<Array<{ name: string; base64: string; mediaType: string }>> {
  const view = new Uint8Array(buffer);
  const results: Array<{ name: string; base64: string; mediaType: string }> = [];
  const imgTypes: Record<string, string> = {
    png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp',
  };
  let i = 0;
  while (i < view.length - 30) {
    if (view[i] !== 0x50 || view[i+1] !== 0x4B || view[i+2] !== 0x03 || view[i+3] !== 0x04) { i++; continue; }
    const compression  = view[i+8]  | (view[i+9]  << 8);
    const compressedSz = view[i+18] | (view[i+19] << 8) | (view[i+20] << 16) | (view[i+21] << 24);
    const filenameLen  = view[i+26] | (view[i+27] << 8);
    const extraLen     = view[i+28] | (view[i+29] << 8);
    const name = new TextDecoder().decode(view.slice(i + 30, i + 30 + filenameLen));
    const dataStart = i + 30 + filenameLen + extraLen;
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    if (name.startsWith(mediaPrefix) && ext in imgTypes) {
      try {
        const raw = view.slice(dataStart, dataStart + compressedSz) as unknown as Uint8Array<ArrayBuffer>;
        const bytes = compression === 0 ? raw : await decompressDeflateRaw(raw);
        results.push({ name, base64: uint8ToBase64(bytes), mediaType: imgTypes[ext] });
      } catch (_) {}
    }
    i = dataStart + (compressedSz || 1);
  }
  return results;
}

function extractImagesFromPdf(buffer: ArrayBuffer): Array<{ name: string; base64: string; mediaType: string }> {
  const bytes = new Uint8Array(buffer);
  const results: Array<{ name: string; base64: string; mediaType: string }> = [];
  for (let i = 0; i < bytes.length - 4; i++) {
    if (bytes[i] === 0xFF && bytes[i+1] === 0xD8 && bytes[i+2] === 0xFF) {
      let end = -1;
      for (let j = i + 4; j < bytes.length - 1; j++) {
        if (bytes[j] === 0xFF && bytes[j+1] === 0xD9) { end = j + 2; break; }
      }
      if (end > i + 100) {
        results.push({ name: `image_${results.length}.jpg`, base64: uint8ToBase64(bytes.slice(i, end)), mediaType: 'image/jpeg' });
        i = end - 1;
      }
    } else if (bytes[i] === 0x89 && bytes[i+1] === 0x50 && bytes[i+2] === 0x4E && bytes[i+3] === 0x47) {
      let end = -1;
      for (let j = i + 8; j < bytes.length - 8; j++) {
        if (bytes[j] === 0x49 && bytes[j+1] === 0x45 && bytes[j+2] === 0x4E && bytes[j+3] === 0x44) { end = j + 8; break; }
      }
      if (end > i + 100) {
        results.push({ name: `image_${results.length}.png`, base64: uint8ToBase64(bytes.slice(i, end)), mediaType: 'image/png' });
        i = end - 1;
      }
    }
  }
  return results;
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function extractImagesFromFile(
  file: File
): Promise<Array<{ name: string; base64: string; mediaType: string }>> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'docx') return extractImagesFromZip(await readAsArrayBuffer(file), 'word/media/');
  if (ext === 'xlsx' || ext === 'xls') return extractImagesFromZip(await readAsArrayBuffer(file), 'xl/media/');
  if (ext === 'pdf') return extractImagesFromPdf(await readAsArrayBuffer(file));
  return [];
}

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp']);

async function readImageFile(file: File): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = e => {
      const dataUrl = e.target!.result as string;
      const commaIdx = dataUrl.indexOf(',');
      resolve({
        base64: dataUrl.slice(commaIdx + 1),
        mediaType: dataUrl.slice(0, commaIdx).match(/data:([^;]+);/)?.[1] ?? 'image/png',
      });
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export async function extractDocContent(file: File): Promise<DocContent> {
  const ext = file.name.split('.').pop()!.toLowerCase();
  if (['txt', 'csv', 'md', 'text'].includes(ext)) return { text: await readAsText(file), images: [] };
  if (IMAGE_EXTENSIONS.has(ext)) return { text: '', images: [await readImageFile(file)] };
  const buffer = await readAsArrayBuffer(file);
  if (ext === 'pdf')  return extractPdfContent(buffer);
  if (ext === 'docx') return extractDocxContent(buffer);
  if (ext === 'xlsx' || ext === 'xls') return extractXlsxContent(buffer);
  throw new Error(`Unsupported format: .${ext}. Supported: PDF, Word (.docx), Excel (.xlsx), TXT, CSV, or an image.`);
}

export function isImageFile(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return IMAGE_EXTENSIONS.has(ext);
}

export async function extractImageData(file: File): Promise<ImageData> {
  return readImageFile(file);
}

export async function extractTextFromFile(file: File): Promise<string> {
  const { text } = await extractDocContent(file);
  return text;
}
